import React, { useEffect, useRef } from 'react';
import { Box, Avatar, Typography, Link as MuiLink } from '@mui/material';
import { FRONTEND_BASE_URL } from 'utils/apiUtils';
import avatarPlaceholder from 'images/avatar-cropped.png';
import { initializeQRCode } from 'utils/emailSignature/copyEmailSignature';
import getIconForType from 'utils/icons/getIconForType';

const TeamEmailSignatureContent = React.forwardRef(({ emailSignatureState, cardId, setLoading, setError }, ref) => {
    const qrRef = useRef(null);
    const qrContainerRef = useRef(null);
    const cardUrl = `${FRONTEND_BASE_URL}/card/${cardId}`;

    // Debug: Log the email signature state
    useEffect(() => {
        if (cardId && emailSignatureState[cardId]) {
            console.log('🔍 TeamEmailSignatureContent: Email signature state for cardId:', cardId, {
                name: emailSignatureState[cardId].emailSignatureName,
                jobTitle: emailSignatureState[cardId].emailSignatureJobTitle,
                company: emailSignatureState[cardId].emailSignatureCompany,
                location: emailSignatureState[cardId].emailSignatureLocation,
                phoneNumber: emailSignatureState[cardId].emailSignaturePhoneNumber,
                profilePictureUrl: emailSignatureState[cardId].emailSignatureProfilePictureUrl,
                companyLogoUrl: emailSignatureState[cardId].emailSignatureCompanyLogoUrl,
                includeQRCode: emailSignatureState[cardId].emailSignatureIncludeQRCode
            });
        } else {
            console.warn('🔍 TeamEmailSignatureContent: No email signature state found for cardId:', cardId);
        }
    }, [cardId, emailSignatureState]);

    useEffect(() => {
        const setupQRCode = async () => {
            if (!emailSignatureState[cardId]?.emailSignatureIncludeQRCode) {
                setLoading(false);
                return;
            }

            try {
                await initializeQRCode({
                    qrContainerRef,
                    qrRef,
                    cardUrl,
                    emailSignatureState,
                    cardId
                });
                setLoading(false);
            } catch (error) {
                console.error('QR setup error:', error);
                setError('Failed to generate QR code');
                setLoading(false);
            }
        };

        setupQRCode();
    }, [cardUrl, emailSignatureState, cardId, setLoading, setError]);

    const hasProfilePicture = !!emailSignatureState[cardId]?.emailSignatureProfilePictureUrl;
    const hasCompanyLogo = !!emailSignatureState[cardId]?.emailSignatureCompanyLogoUrl;
    const profilePictureUrl = emailSignatureState[cardId]?.emailSignatureProfilePictureUrl || avatarPlaceholder;

    const renderProfileSection = () => {
        const showProfilePic = emailSignatureState[cardId]?.emailSignatureShowProfilePic !== false;
        const showCompanyLogo = emailSignatureState[cardId]?.emailSignatureShowCompanyLogo !== false;

        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            }}>
                {/* Profile picture - show if toggle is on */}
                {showProfilePic && (
                    <Box sx={{ position: 'relative', display: 'inline-block', marginBottom: 2 }}>
                        <Avatar
                            src={profilePictureUrl}
                            sx={{
                                width: '90px',
                                height: '90px',
                                backgroundColor: '#ffffff',
                                border: '2px solid #e0e0e0',
                            }}
                        />
                        {/* Show company logo as overlay when company logo exists and toggle is on */}
                        {showCompanyLogo && hasCompanyLogo && (
                            <Avatar
                                src={emailSignatureState[cardId]?.emailSignatureCompanyLogoUrl}
                                sx={{
                                    width: '40px',
                                    height: '40px',
                                    backgroundColor: '#ffffff',
                                    border: '2px solid #e0e0e0',
                                    position: 'absolute',
                                    bottom: 0,
                                    right: 0,
                                    transform: 'translate(20%, 20%)',
                                }}
                            />
                        )}
                    </Box>
                )}

                {/* Text content */}
                <Box sx={{ mt: 2 }}>
                    {emailSignatureState[cardId]?.emailSignatureShowName !== false && emailSignatureState[cardId]?.emailSignatureName && (
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                            {emailSignatureState[cardId].emailSignatureName}
                        </Typography>
                    )}

                    {emailSignatureState[cardId]?.emailSignatureShowJobTitle !== false && emailSignatureState[cardId]?.emailSignatureJobTitle && (
                        <Typography variant="body1" sx={{ fontSize: '0.7rem', color: '#4f4f4f' }}>
                            {emailSignatureState[cardId].emailSignatureJobTitle}
                        </Typography>
                    )}

                    {emailSignatureState[cardId]?.emailSignatureShowCompany !== false && emailSignatureState[cardId]?.emailSignatureCompany && (
                        <Typography variant="body1" sx={{ fontSize: '0.7rem', color: '#4f4f4f' }}>
                            {emailSignatureState[cardId].emailSignatureCompany}
                        </Typography>
                    )}

                    {emailSignatureState[cardId]?.emailSignatureShowLocation !== false && emailSignatureState[cardId]?.emailSignatureLocation && (
                        <Typography variant="body1" sx={{
                            fontSize: '0.7rem',
                            marginTop: '10px',
                            color: '#828282'
                        }}>
                            {emailSignatureState[cardId].emailSignatureLocation}
                        </Typography>
                    )}

                    {emailSignatureState[cardId]?.emailSignatureShowPhoneNumber !== false && emailSignatureState[cardId]?.emailSignaturePhoneNumber && (
                        <Typography variant="body1" sx={{ fontSize: '0.7rem', color: '#828282' }}>
                            {emailSignatureState[cardId].emailSignaturePhoneNumber}
                        </Typography>
                    )}

                    {/* Contact Link Icons - Display horizontally at the bottom of text content */}
                    {emailSignatureState[cardId]?.emailSignatureLinkTypes && emailSignatureState[cardId].emailSignatureLinkTypes.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                            {emailSignatureState[cardId].emailSignatureLinkTypes.map((linkType) => {
                                const iconElement = getIconForType(linkType);
                                return (
                                    <Box
                                        key={linkType}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: '4px',
                                            backgroundColor: '#f0f0f0',
                                            borderRadius: '4px',
                                            flexShrink: 0
                                        }}
                                    >
                                        {React.cloneElement(iconElement, {
                                            sx: {
                                                ...(iconElement.props?.sx || {}),
                                                fontSize: '20px',
                                                color: '#666',
                                            },
                                        })}
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderQRSection = () => (
        emailSignatureState[cardId]?.emailSignatureIncludeQRCode && (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: 1
            }}>
                <Typography variant="body1" sx={{ fontSize: '0.7rem', fontWeight: '500', color: '#828282' }}>
                    Connect with me
                </Typography>

                <Box
                    id="qrContainerRef"
                    ref={qrContainerRef}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 'auto',
                        marginTop: '20px',
                        '& canvas, & svg': {
                            maxWidth: '140px',
                            height: 'auto',
                        }
                    }}
                />

                <MuiLink
                    href={`/card/${cardId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: '#2aaff7',
                        textDecoration: 'none',
                        marginBottom: 2,
                        fontSize: '0.8rem',
                        fontWeight: '500',
                    }}
                >
                    My Digital Business Card
                </MuiLink>
            </Box>
        )
    );

    return (
        <Box ref={ref}>
            <Box
                display="flex"
                flexDirection="column"
                sx={{
                    padding: 2,
                    backgroundColor: 'white',
                    maxWidth: '600px',
                    margin: 'auto',
                    position: 'relative',
                }}
            >
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start' // Align items at the top to ensure proper alignment when no profile picture
                }}>
                    {renderProfileSection()}
                    {renderQRSection()}
                </Box>

                <Box sx={{ mt: 1 }}>
                    {emailSignatureState[cardId]?.emailSignatureIncludeBannerImage &&
                        emailSignatureState[cardId]?.emailSignatureBannerImageUrl && (
                            <Box sx={{ mb: 1 }}>
                                <img
                                    src={emailSignatureState[cardId]?.emailSignatureBannerImageUrl}
                                    alt="Banner"
                                    style={{ width: '100%', height: 'auto' }}
                                />
                            </Box>
                        )}

                    {emailSignatureState[cardId]?.emailSignatureDisclaimer && (
                        <Typography variant="body1" sx={{ fontSize: '0.7rem', marginTop: '10px', color: '#bdbdbd', fontStyle: 'italic' }}>
                            {emailSignatureState[cardId].emailSignatureDisclaimer}
                        </Typography>
                    )}
                </Box>
            </Box>
        </Box>
    );
});

export default TeamEmailSignatureContent;